import { useMemo, Fragment } from 'react'
import { Text } from '@chakra-ui/react'
import { useTranslation, Trans } from 'next-i18next'

import { logEvent } from '@/lib/gtm'
import env from '@/lib/env'

import { TEXT_STYLE } from '@/features/landing/constants/FAQs'
import { Item as FAQItem } from '@/features/landing/components/FAQs'
import { textStyle } from '@/features/landing/components/Carousel/Contents/style'

import type { HeroProps } from '@/features/services/templates/Hero'
import type { ServiceInfoProps } from '@/features/services/templates/ServiceInfo'
import type { TextWithImageProps } from '@/features/services/templates/TextWithImage'

import DesktopBgImage from '@/images/services/compliance/banner-desktop.jpg'
import MobileBgImage from '@/images/services/compliance/banner-mobile.jpg'
import InfoImage from '@/images/services/compliance/info.png'
import FloatImage from '@/images/services/compliance/floating.jpg'

import type { WidgetProps, IconTextProps } from '../types'
import { TARGET_TEXT_STYLE } from './style'

export const useMeta = (): PageMeta => {
  const { t } = useTranslation('services')

  return {
    title: t('compliance.meta.title'),
    description: t('compliance.meta.description'),
    og: {
      title: t('compliance.meta.og.title'),
      description: t('compliance.meta.og.description'),
      image: {
        url: `${env.APP_URL}${t('compliance.meta.og.image.url')}`,
      },
    },
  }
}

export const useHeroData = (): HeroProps => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => ({
      title: t('compliance.title'),
      subtitle: t('compliance.subtitle'),
      image: {
        base: `url(${MobileBgImage.src})`,
        md: `url(${DesktopBgImage.src})`,
      },
      titleColor: 'white',
      subtitleColor: 'white',
      colorSchemePrimary: 'primary',
      colorSchemeSecondary: 'white',
      bgColor: 'secondary.800',
      onClickGetQuote: (): void => {
        logEvent({
          ga: {
            category: 'Compliance',
            action: 'Click get quote',
          },
        })
      },
      onClickGetHandout: (): void => {
        logEvent({
          ga: {
            category: 'Compliance',
            action: 'Download handout',
            label: 'header',
          },
        })
      },
    }),
    [language],
  )
}

export const useServiceInfoData = (): ServiceInfoProps => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => ({
      title: t('compliance.serviceInfo.title'),
      paragraph: (
        <Trans t={t} i18nKey="compliance.serviceInfo.paragraph">
          ดำเนินการตรวจสอบในมุมมองทางกฎหมายสำหรับกระบวนการทำงาน มาตรการที่มีอยู่
          นโยบายในการคุ้มครองข้อมูลส่วนบุคคลของบริษัทรวมถึงเอกสารที่เกี่ยวข้องกับข้อมูลส่วนบุคคลทั้งหมด
          เพื่อนำมาจัดทำ Gap Analysis Report
          และให้คำแนะนำเกี่ยวกับแนวทางการปฏิบัติให้สอดคล้องกับ PDPA
          โดยนักกฎหมายผู้เชี่ยวชาญ
          <Fragment>
            <br />
            <br />
          </Fragment>
          เนื่องจากการปฏิบัติตามพ.ร.บ. คุ้มครองข้อมูลส่วนบุคคล
          มีความซับซ้อนและแตกต่างในแต่ละธุรกิจ
          องค์กรจึงควรปรึกษาผู้เชี่ยวชาญเพื่อตรวจสอบความถูกต้องและ
          ป้องกันบทลงโทษทางกฎหมายที่อาจเกิดขึ้น
        </Trans>
      ),
      image: InfoImage,
    }),
    [language],
  )
}

export const useFeatureData = (): IconTextProps[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => [
      {
        image: '/images/services/compliance/features-1.svg',
        text: t('compliance.features.contents.0'),
      },
      {
        image: '/images/services/compliance/features-2.svg',
        text: t('compliance.features.contents.1'),
      },
      {
        image: '/images/services/compliance/features-3.svg',
        text: t('compliance.features.contents.2'),
      },
      {
        image: '/images/services/compliance/features-4.svg',
        text: t('compliance.features.contents.3'),
      },
    ],
    [language],
  )
}

export const useWhatYouWillGetData = (): WidgetProps[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => [
      {
        image: '/images/services/compliance/what-you-will-get-1.svg',
        text: t('compliance.whatYouWillGet.contents.0'),
      },
    ],
    [language],
  )
}

export const useTargetData = (): TextWithImageProps => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => ({
      title: t('compliance.target.title'),
      paragraph: (
        <Text sx={{ ...textStyle, ...TARGET_TEXT_STYLE }}>
          {t('compliance.target.paragraph')}
        </Text>
      ),
      image: FloatImage,
      imageRatio: 675 / 363,
    }),
    [language],
  )
}

export const useFAQsData = (): FAQItem[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('faqs')

  return useMemo(
    () => [
      {
        title: t('FAQs.5.question'),
        content: <Text sx={TEXT_STYLE}>{t('FAQs.5.answer.paragraph')}</Text>,
      },
    ],
    [language],
  )
}

export const usePricingData = (): React.ReactNode[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(() => [t('compliance.pricing.contents.0')], [language])
}

export const useWhyUsData = (): React.ReactNode[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(() => [t('compliance.whyUs.contents.0')], [language])
}
